import { Image } from 'expo-image';
import { useEffect, useState } from 'react';
import { Linking, Platform, Pressable, ScrollView, View } from 'react-native';
import Modal from 'react-native-modal';
import { createStyleSheet, useStyles } from 'react-native-unistyles';

import type { CustomerDocumentAsset } from '@/api/partners/organizations/types';
import {
  NavigationButton,
  NavigationButtonTypes,
} from '@/components/navigation-button';
import { Text } from '@/ui';
import { Button } from '@/ui/button';
import { Icon } from '@/ui/icon';
import { ArrowRight, Docs } from '@/ui/icons';
import { ArrowLeft } from '@/ui/icons/arrow-left';
import { DownloadCircle } from '@/ui/icons/download-circle';
import { Loader } from '@/ui/loader';

import { PdfFrame } from './pdf-frame';

type Props = {
  isVisible: boolean;
  assets: CustomerDocumentAsset[];
  isLoading: boolean;
  onClose: () => void;
};

const isPdf = (asset: CustomerDocumentAsset) =>
  asset.contentType?.includes('pdf');

const downloadAsset = (asset: CustomerDocumentAsset) => {
  // S3 returns Content-Disposition: attachment on downloadUrl, so an anchor
  // downloads cross-origin without a fetch/CORS round-trip.
  if (Platform.OS !== 'web' || typeof document === 'undefined') {
    Linking.openURL(asset.downloadUrl);
    return;
  }

  const anchor = document.createElement('a');
  anchor.href = asset.downloadUrl;
  anchor.download = asset.filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
};

export const CustomerDocumentsModal = ({
  isVisible,
  assets,
  isLoading,
  onClose,
}: Props) => {
  const { styles, theme } = useStyles(stylesheet);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    if (isVisible) {
      setActiveIndex(0);
    }
  }, [isVisible, assets.length]);

  const activeAsset = assets[activeIndex];

  const goPrev = () => {
    setActiveIndex((prev) => (prev === 0 ? prev : prev - 1));
  };

  const goNext = () => {
    setActiveIndex((prev) => (prev >= assets.length - 1 ? prev : prev + 1));
  };

  const renderBody = () => {
    if (isLoading) {
      return (
        <View style={styles.centered}>
          <Loader style={{ height: 200, width: 200 }} />
        </View>
      );
    }

    if (!activeAsset) {
      return (
        <View style={styles.centered}>
          <Text
            type="bodyRegular"
            style={{ color: theme.colors.text.secondary }}
          >
            No documents available.
          </Text>
        </View>
      );
    }

    return (
      <>
        <View style={styles.viewerRow}>
          <Pressable
            onPress={goPrev}
            disabled={activeIndex === 0}
            style={{ opacity: activeIndex === 0 ? 0.3 : 1 }}
          >
            <Icon
              type="dressed"
              icon={<ArrowLeft />}
              fillColor={theme.colors.icon.color}
            />
          </Pressable>

          <View style={styles.viewer}>
            {isPdf(activeAsset) ? (
              <PdfFrame url={activeAsset.url} />
            ) : (
              <Image
                style={{ width: '100%', height: '100%' }}
                source={activeAsset.url}
                contentFit="contain"
              />
            )}
          </View>

          <Pressable
            onPress={goNext}
            disabled={activeIndex >= assets.length - 1}
            style={{ opacity: activeIndex >= assets.length - 1 ? 0.3 : 1 }}
          >
            <Icon
              type="dressed"
              icon={<ArrowRight />}
              fillColor={theme.colors.icon.color}
            />
          </Pressable>
        </View>

        {assets.length > 1 ? (
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={styles.thumbnails}
          >
            {assets.map((asset, idx) => (
              <Pressable
                key={`${asset.url}-${idx}`}
                onPress={() => setActiveIndex(idx)}
                style={[
                  styles.thumbnail,
                  idx === activeIndex && styles.thumbnailActive,
                ]}
              >
                {isPdf(asset) ? (
                  <View style={styles.thumbnailPdf}>
                    <Icon
                      type="naked"
                      icon={<Docs />}
                      fillColor={theme.colors.icon.color}
                      width={24}
                      height={24}
                    />
                  </View>
                ) : (
                  <Image
                    source={asset.url}
                    contentFit="cover"
                    style={{ width: '100%', height: '100%' }}
                  />
                )}
              </Pressable>
            ))}
          </ScrollView>
        ) : null}
      </>
    );
  };

  return (
    <Modal
      animationIn="slideInUp"
      animationInTiming={300}
      animationOut="slideOutDown"
      animationOutTiming={300}
      isVisible={isVisible}
      hasBackdrop
      onBackdropPress={onClose}
    >
      <View style={styles.centerWrapper}>
        <View style={styles.card}>
          <View style={styles.header}>
            <Text type="title2" numberOfLines={1} style={styles.title}>
              {activeAsset?.filename ?? ''}
            </Text>

            <View style={styles.headerActions}>
              {activeAsset ? (
                <Button
                  type="secondarySmall"
                  text="Download"
                  onPress={() => downloadAsset(activeAsset)}
                  style={{ width: 'auto' }}
                  rightIcon={
                    <View style={styles.downloadIcon}>
                      <DownloadCircle />
                    </View>
                  }
                />
              ) : null}

              <NavigationButton
                type={NavigationButtonTypes.cancel}
                onPress={onClose}
              />
            </View>
          </View>

          {renderBody()}
        </View>
      </View>
    </Modal>
  );
};

const VIEWER_HEIGHT = 440;

const stylesheet = createStyleSheet((theme) => ({
  centerWrapper: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  card: {
    width: 820,
    maxWidth: '95%',
    backgroundColor: theme.colors.background_2,
    borderRadius: 16,
    padding: 16,
    gap: 16,
    // Prevent the browser from selecting the image/arrows on rapid clicks,
    // which otherwise paints a translucent selection highlight over the viewer.
    userSelect: 'none',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    minHeight: 40,
  },
  title: {
    flex: 1,
    textAlign: 'center',
    paddingHorizontal: 200,
  },
  headerActions: {
    position: 'absolute',
    right: 0,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  downloadIcon: {
    width: 18,
    height: 18,
  },
  centered: {
    height: VIEWER_HEIGHT,
    alignItems: 'center',
    justifyContent: 'center',
  },
  viewerRow: {
    height: VIEWER_HEIGHT,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  viewer: {
    flex: 1,
    height: '100%',
    borderRadius: 8,
    overflow: 'hidden',
  },
  thumbnails: {
    gap: 12,
    paddingVertical: 4,
    justifyContent: 'flex-start',
  },
  thumbnail: {
    width: 64,
    height: 64,
    borderRadius: 8,
    overflow: 'hidden',
    borderWidth: 2,
    borderColor: 'transparent',
    backgroundColor: theme.colors.background_1,
  },
  thumbnailActive: {
    borderColor: theme.colors.icon.successColor,
  },
  thumbnailPdf: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
}));
